import * as ReactDOM from 'react-dom';
import * as React from 'react';
import { FILTER_TYPE_MAPPING, VIEW_MODE_AVAILABLE_OPTIONS } from '../constants';
import { createBikeSearchReducer } from '../reducer';
import useApiQueryDataUpdate from '../../shop-ui/components/search-page/hooks/useApiQueryDataUpdate';
import SortFilterSidebar from '../../shop-ui/components/search-page/components/sidebar/SortFilterSidebar';
import FilterStateService from '../../shop-ui/components/search-page/services/FilterStateService';
import { hasFiltersSelected, initSearchPageData } from '../../shop-ui/components/search-page/actions/searchPage';
import Header from '../../shop-ui/components/search-page/components/header/Header';
import Footer from '../../shop-ui/components/search-page/components/footer/Footer';
import useViewModeSwitchUpdate from '../../shop-ui/components/search-page/hooks/useViewModeSwitchUpdate';
import { ISearchPageActionState } from '../../shop-ui/components/search-page/interfaces';
import { PARAM_PER_PAGE, VIEW_MODE_LIST } from '../../shop-ui/components/search-page/constants';
import { useEnhancedReducer } from '../../shop-ui/includes/enhanced-reducer';
import { numberFormat } from '../../react/common/lib';
import { useTranslationHook } from 'PyzShopUi/scripts/utils/translationHook';
import Loading from 'PyzShopUi/scripts/react/common/components/Loading';


interface IBikeSearchPageProps {
    apiUrl: string;
    defaultParameter: {
        viewMode: string;
    };
    filterStateService: FilterStateService;
}

const emptyBikeSearchPageState: ISearchPageActionState = {
    sort: {
        sortParamNames: [],
        currentSortParam: '',
    },
    pagination: {
        numFound: 0,
        currentPage: 1,
        maxPage: 1,
        currentItemsPerPage: 0,
    },
    searchResultItems: [],
    filters: [],
    isPageLoading: false,
    apiQueryData: {},
    updatePageDebounceTime: 0,
    skipUpdateBaseOnApiQueryParams: false,
};

export const BikeSearchPage: React.FunctionComponent<IBikeSearchPageProps> = props => {
    const [state, dispatch] = useEnhancedReducer(createBikeSearchReducer(props.filterStateService), emptyBikeSearchPageState);
    const {
        pagination,
        sort,
        isPageLoading,
        filters,
        apiQueryData,
        updatePageDebounceTime,
        skipUpdateBaseOnApiQueryParams,
        viewMode
    } = state;

    const shouldRenderSortSelection = (): boolean => {
        if (viewMode === VIEW_MODE_LIST) {
            return false;
        }
        if (!sort) {
            return false;
        }
        return sort.sortParamNames.length !== 0;
    }

    const renderPerPageSelection: boolean = false;
    const renderSortSelection: boolean = shouldRenderSortSelection();
    const availableViewModes: string[] = VIEW_MODE_AVAILABLE_OPTIONS;
    const itemPerPageOptions: number[] = pagination?.config?.validItemsPerPageOptions || [];
    const currentItemsPerPage: string = pagination?.currentItemsPerPage.toString() || '';
    const stateAsQueryParamMap: Map<string, string> = (new Map()).set('viewMode', 'bike_view');
    const emptyFilterSearchApiSuffix: string = 'popular';
    const { translate } = useTranslationHook();

    useApiQueryDataUpdate(apiQueryData, dispatch, updatePageDebounceTime, props.filterStateService, skipUpdateBaseOnApiQueryParams, [PARAM_PER_PAGE]);
    useViewModeSwitchUpdate(apiQueryData, viewMode, dispatch, updatePageDebounceTime, props.filterStateService, [PARAM_PER_PAGE], stateAsQueryParamMap);

    React.useEffect(() => {
        dispatch(initSearchPageData(props.apiUrl, props.defaultParameter, props.filterStateService, [PARAM_PER_PAGE], emptyFilterSearchApiSuffix));
    }, []);

    const displaySidebar = (): React.ReactPortal => (
        ReactDOM.createPortal(
            <React.Suspense fallback={<Loading />}>
            <SortFilterSidebar
                apiQueryData={apiQueryData}
                pagination={pagination}
                sort={sort}
                filters={filters}
                filtersMapping={FILTER_TYPE_MAPPING}
                filterStateService={props.filterStateService}
                isProgressing={isPageLoading}
                renderTextSearchField={true}
                renderSortSelection={renderSortSelection}
                renderPerPageSelection={renderPerPageSelection}
                perPageAndSortHeadline={translate('Sorting', { ns: 'search-page-filter'})}
                filtersHeadlineText={translate('Find your bike', { ns: 'bike-db'})}
                filtersHeadlineTextSmall={translate('Find your bike & Sorting', { ns: 'bike-db'})}
                filtersShowResultsText={'showBike'}
                dispatch={dispatch}
            />
            </React.Suspense>,
            document.querySelector('.bike-search-layout-sidebar'),
        )
    );

    const getSearchResultText = (hasCurrentlyFiltersSelected: boolean): string => {
        if (!hasCurrentlyFiltersSelected) {
            return translate('Most popular bikes', { ns: 'bike-db'});
        }

        const pageNumFound = pagination?.numFound || 0;

        return translate('bike', {
            count: pageNumFound,
            ns: 'bike-db'
        });
    }

    const displayResultHeader = () => {
        if (isPageLoading) {
            return null;
        }

        const hasCurrentlyFiltersSelected: boolean = hasFiltersSelected({ ...apiQueryData });
        const totalSearchResultItemsText: string = getSearchResultText(hasCurrentlyFiltersSelected);

        const sortParamNames: string[] = sort?.sortParamNames || [];
        const currentSortParam: string = sort?.currentSortParam || 'capacity';

        return ReactDOM.createPortal(
            <Header
                itemPerPageOptions={itemPerPageOptions}
                viewMode={viewMode}
                availableViewModes={availableViewModes}
                currentItemsPerPage={currentItemsPerPage}
                totalSearchResultItemsText={totalSearchResultItemsText}
                sortParamNames={sortParamNames}
                renderPerPageSelection={renderPerPageSelection}
                renderSortSelection={renderSortSelection}
                renderViewModeSwitch={hasCurrentlyFiltersSelected}
                currentSortParam={currentSortParam}
                dispatch={dispatch}
            />,
            document.querySelector('.result-header')
        )
    }

    const displayResultFooter = () => {
        if (isPageLoading) {
            return null;
        }

        const currentPage: number = pagination?.currentPage || 0;
        const maxPage: number = pagination?.maxPage || 0;

        return ReactDOM.createPortal(
            <Footer
                perPageSelectText={translate('Bikes per page', { ns: 'search'})}
                itemPerPageOptions={itemPerPageOptions}
                renderPerPageSelection={renderPerPageSelection}
                currentItemsPerPage={currentItemsPerPage}
                currentPage={currentPage}
                maxPage={maxPage}
                dispatch={dispatch}
            />,
            document.querySelector('.result-footer')
        )
    }

    return (
        <React.Fragment>
            {displayResultHeader()}
            {displaySidebar()}
            {displayResultFooter()}
        </React.Fragment>
    );
}
